import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useHasRole } from '~/shared/context/use-has-role';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import { Morph } from '~/ui/animations/morph/Morph';
import { Badge } from '~/ui/core/badge/Badge';
import { IconCheckmark1Small } from '~/ui/core/icons';

import { resolveWorkflowState } from '../hooks/use-job-profile-workflow';
import { usePublishAction } from '../hooks/use-publish-action';
import { useReviewAction } from '../hooks/use-review-action';
import { InviteTeamMemberForJobItem } from './InviteTeamMemberForJobItem';
import { GenericAssignReviewerBadge } from './ReviewerComponents';

type JobItem = JobLevel | JobRole;

type ItemType = 'job-level' | 'job-role';

type JobProfileStatusBadgeProps = {
    readonly item: JobItem;
    readonly itemType: ItemType;
    readonly jobArchitectureId: string;
    readonly jobClusterId?: string;
};

const animations = {
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    initial: false,
    transition: { delay: 0.5, duration: 0.7 },
};

type BadgeState = 'assign-reviewer' | 'review' | 'publish';

const resolveBadgeState = (item: JobItem): BadgeState | null => {
    const state = resolveWorkflowState(item, { ignoreGeneration: true });

    switch (state) {
        case 'assign-reviewer':
        case 'review':
        case 'publish':
            return state;
        case 'archived':
        case 'generating':
        case 'none':
            return null;
    }
};

// Callers use this to decide layout before rendering the badge; the repeated
// resolveBadgeState call inside the component is cheap and intentional.
export const hasJobProfileStatusBadge = (item: JobItem): boolean => resolveBadgeState(item) !== null;

const ReviewBadge: FunctionComponent<{
    readonly item: JobItem;
    readonly itemType: ItemType;
    readonly jobArchitectureId: string;
}> = ({ item, itemType, jobArchitectureId }) => {
    const { translate } = useContext(ApplicationContext);
    const { handleReview } = useReviewAction({ architectureId: jobArchitectureId, item, itemType });

    return (
        <Badge data-testid="review-badge" variant="info" onClick={handleReview}>
            {translate('pages.job-architecture.mark-as-reviewed')}
        </Badge>
    );
};

const PublishBadge: FunctionComponent<{
    readonly item: JobItem;
    readonly itemType: ItemType;
    readonly jobArchitectureId: string;
}> = ({ item, itemType, jobArchitectureId }) => {
    const { translate } = useContext(ApplicationContext);
    const { handlePublish } = usePublishAction({ architectureId: jobArchitectureId, item, itemType });
    const [isDone, setIsDone] = useState(false);

    const onPublish = async (): Promise<void> => {
        await handlePublish();
        setIsDone(true);
    };

    return (
        <Badge data-testid="publish-badge" style={{ overflow: 'hidden' }} variant="success" onClick={onPublish}>
            <Morph stateKey={isDone ? 'done' : 'working-on-it'}>
                {isDone ? (
                    <IconCheckmark1Small size={16} />
                ) : (
                    translate('pages.job-architecture-v2.job-role-publish-button')
                )}
            </Morph>
        </Badge>
    );
};

export const JobProfileStatusBadge: FunctionComponent<JobProfileStatusBadgeProps> = ({
    item,
    itemType,
    jobArchitectureId,
    jobClusterId,
}) => {
    const badgeState = resolveBadgeState(item);
    const isJobArchitect = useHasRole('job_architect');

    const renderAssignReviewerBadge = (onInvite?: () => void): React.ReactNode => (
        <GenericAssignReviewerBadge
            item={item}
            itemType={itemType}
            jobArchitectureId={jobArchitectureId}
            jobClusterId={jobClusterId}
            showOnlyWhenUnassigned={true}
            onInvite={onInvite}
        />
    );

    const renderBadge = (): React.ReactNode => {
        switch (badgeState) {
            case 'assign-reviewer':
                return isJobArchitect ? (
                    <InviteTeamMemberForJobItem architectureId={jobArchitectureId} itemId={item.id} itemType={itemType}>
                        {({ openInviteDialog }) => renderAssignReviewerBadge(openInviteDialog)}
                    </InviteTeamMemberForJobItem>
                ) : (
                    renderAssignReviewerBadge()
                );
            case 'review':
                return (
                    <motion.div {...animations} onClick={(e) => e.stopPropagation()}>
                        <ReviewBadge item={item} itemType={itemType} jobArchitectureId={jobArchitectureId} />
                    </motion.div>
                );
            case 'publish':
                return (
                    <motion.div {...animations} onClick={(e) => e.stopPropagation()}>
                        <PublishBadge item={item} itemType={itemType} jobArchitectureId={jobArchitectureId} />
                    </motion.div>
                );
            case null:
                return null;
        }
    };

    return <AnimatePresence mode="wait">{renderBadge()}</AnimatePresence>;
};
